import { Component } from '@theme/component';

/** Native theme enhancement: no commerce requests, storage, analytics, or new dependencies. */
class SsMascot extends Component {
  /** @type {{ trigger?: HTMLButtonElement, caption?: HTMLElement }} */
  refs;
  timer;

  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  setup() {
    clearTimeout(this.timer);
    if (this.refs.trigger) {
      this.refs.trigger.disabled = false;
      this.refs.trigger.classList.remove('is-squishing');
    }
    if (this.refs.caption) this.refs.caption.textContent = this.refs.caption.dataset.initial || '';
  }

  /** @param {MouseEvent} event */
  squish(event) {
    if (!(event.target instanceof Element) || !this.refs.trigger || !this.refs.caption) return;
    clearTimeout(this.timer);
    // Restart the finite CSS animation for every tap, even within the caption timeout.
    this.refs.trigger.classList.remove('is-squishing');
    void this.refs.trigger.offsetWidth;
    this.refs.trigger.classList.add('is-squishing');
    this.refs.caption.textContent = this.refs.caption.dataset.active || '';
    this.timer = setTimeout(() => this.setup(), 1800);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    super.disconnectedCallback();
  }
}

class SsFaq extends Component {
  /** @type {{ searchTools?: HTMLElement, search?: HTMLInputElement, status?: HTMLElement, empty?: HTMLElement, clearButton?: HTMLButtonElement }} */
  refs;
  listeners;

  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  setup() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    if (this.refs.searchTools) this.refs.searchTools.hidden = false;
    this.filter();
    window.addEventListener('pageshow', () => this.filter(), { signal: this.listeners.signal });
  }

  /** @param {InputEvent} [event] */
  filter(event) {
    const query = this.refs.search?.value.trim().toLocaleLowerCase() || '';
    if (this.refs.clearButton) this.refs.clearButton.hidden = !query;
    let count = 0;
    for (const item of this.querySelectorAll('[data-ss-question]')) {
      const matches = !query || (item.textContent || '').toLocaleLowerCase().includes(query);
      item.hidden = !matches;
      if (matches) count += 1;
    }
    for (const group of this.querySelectorAll('[data-ss-group]')) {
      group.hidden = ![...group.querySelectorAll('[data-ss-question]')].some(item => !item.hidden);
    }
    if (this.refs.empty) this.refs.empty.hidden = count > 0;
    if (this.refs.status) {
      this.refs.status.textContent = count === 1
        ? this.refs.status.dataset.one || ''
        : (this.refs.status.dataset.many || '').replace('{count}', String(count));
    }
  }

  /** @param {MouseEvent} event */
  clear(event) {
    if (!this.refs.search) return;
    this.refs.search.value = '';
    this.filter();
    this.refs.search.focus();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    super.disconnectedCallback();
  }
}

class SsMerch extends Component {
  /** @type {{ dialog?: HTMLDialogElement, dialogTitle?: HTMLElement, dialogFrame?: HTMLElement, dialogSelection?: HTMLElement }} */
  refs;
  listeners;
  opener;

  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  setup() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    for (const control of this.querySelectorAll('[data-ss-enhanced]')) control.hidden = false;
    for (const fallback of this.querySelectorAll('[data-ss-fallback]')) fallback.hidden = true;
    for (const concept of this.querySelectorAll('article[data-ss-concept]')) {
      this.updateColor(concept);
      this.setView(concept, 'full');
      this.setUnit(concept, 'in');
    }
    this.refs.dialog?.addEventListener('close', () => {
      if (this.opener?.isConnected) this.opener.focus();
    }, { signal: this.listeners.signal });
    window.addEventListener('pageshow', () => {
      for (const concept of this.querySelectorAll('article[data-ss-concept]')) this.updateColor(concept);
    }, { signal: this.listeners.signal });
  }

  /** @param {Event} event */
  changeColor(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.checked) return;
    const concept = event.target.closest('article[data-ss-concept]');
    if (!concept || !this.contains(concept)) return;
    this.updateColor(concept);
    this.setView(concept, 'full');
  }

  /** @param {Element} concept */
  updateColor(concept) {
    const radio = concept.querySelector('input[type="radio"]:checked');
    if (!(radio instanceof HTMLInputElement)) return;
    // Values originate from fixed 1..3 controls, not interpolated merchant strings.
    const template = [...concept.querySelectorAll('template[data-ss-color]')].find(t => t.dataset.ssColor === radio.value);
    const image = template instanceof HTMLTemplateElement ? template.content.querySelector('img') : null;
    const frame = concept.querySelector('[data-ss-frame]');
    const link = concept.querySelector('.ss-merch__enlarge');
    if (!image || !frame || !(link instanceof HTMLAnchorElement)) return;
    frame.replaceChildren(image.cloneNode(true));
    link.href = template.dataset.url || image.src;
    const note = template.dataset.note || '';
    const status = concept.querySelector('[data-ss-color-status]');
    if (status instanceof HTMLElement) {
      status.textContent = (status.dataset.format || '').replace('{color}', template.dataset.label || '') + (note ? ` ${note}` : '');
    }
    const sizeNote = concept.querySelector('[data-ss-supplier-note]');
    if (sizeNote instanceof HTMLElement) {
      sizeNote.textContent = template.dataset.supplierNote || '';
      sizeNote.hidden = !sizeNote.textContent;
    }
  }

  /** @param {MouseEvent} event */
  changeView(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-view]');
    const owner = button?.closest('dialog, article[data-ss-concept]');
    if (!(button instanceof HTMLButtonElement) || !owner || !this.contains(owner)) return;
    this.setView(owner, button.dataset.view === 'detail' ? 'detail' : 'full');
  }

  /** @param {Element} owner @param {'full'|'detail'} view */
  setView(owner, view) {
    const frame = owner.querySelector('[data-ss-frame]');
    if (frame instanceof HTMLElement) frame.dataset.view = view;
    for (const control of owner.querySelectorAll('button[data-view]')) {
      control.setAttribute('aria-pressed', String(control.dataset.view === view));
    }
    const note = owner.querySelector('[data-ss-view-note]');
    if (note instanceof HTMLElement) note.textContent = view === 'detail' ? note.dataset.detail || '' : note.dataset.full || '';
  }

  /** @param {MouseEvent} event */
  changeUnit(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-unit]');
    const concept = button?.closest('article[data-ss-concept]');
    if (!(button instanceof HTMLButtonElement) || !concept || !this.contains(concept)) return;
    this.setUnit(concept, button.dataset.unit === 'cm' ? 'cm' : 'in');
  }

  /** @param {Element} concept @param {'in'|'cm'} unit */
  setUnit(concept, unit) {
    for (const cell of concept.querySelectorAll('[data-inches]')) {
      const inches = Number(cell.dataset.inches);
      if (!Number.isFinite(inches) || inches <= 0) continue;
      cell.textContent = String(unit === 'cm' ? Math.round(inches * 25.4) / 10 : inches);
    }
    const label = concept.querySelector('[data-ss-unit-label]');
    if (label instanceof HTMLElement) label.textContent = unit === 'cm' ? label.dataset.cm || '' : label.dataset.in || '';
    for (const control of concept.querySelectorAll('button[data-unit]')) control.setAttribute('aria-pressed', String(control.dataset.unit === unit));
  }

  /** @param {MouseEvent} event */
  enlarge(event) {
    if (!(event.target instanceof Element) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('.ss-merch__enlarge');
    const concept = link?.closest('article[data-ss-concept]');
    const dialog = this.refs.dialog;
    if (!(link instanceof HTMLAnchorElement) || !concept || !this.contains(concept) || !dialog || typeof dialog.showModal !== 'function') return;
    const image = concept.querySelector('[data-ss-frame] img');
    if (!image || !this.refs.dialogFrame) return;
    event.preventDefault();
    this.opener = link;
    dialog.dataset.ssConcept = concept.dataset.ssConcept;
    this.refs.dialogFrame.replaceChildren(image.cloneNode(true));
    const heading = concept.querySelector('h2')?.textContent || '';
    const radio = concept.querySelector('input:checked');
    const template = [...concept.querySelectorAll('template')].find(t => t.dataset.ssColor === radio?.value);
    if (this.refs.dialogTitle) this.refs.dialogTitle.textContent = `${heading} · ${template?.dataset.label || ''}`;
    if (this.refs.dialogSelection) this.refs.dialogSelection.textContent = concept.querySelector('[data-ss-color-status]')?.textContent || '';
    this.setView(dialog, concept.querySelector('[data-ss-frame]')?.dataset.view === 'detail' ? 'detail' : 'full');
    if (!dialog.open) dialog.showModal();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    if (this.refs.dialog?.open) this.refs.dialog.close();
    super.disconnectedCallback();
  }
}

for (const [name, constructor] of [['ss-mascot', SsMascot], ['ss-faq', SsFaq], ['ss-merch', SsMerch]]) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
